import L from 'leaflet';
import { Circle, Polygon, Tooltip } from 'react-leaflet';
import {
  getIpawsAreaPolygonCoordinates,
  IPAWS_AREA_TYPES,
} from '../../../../../../utils/ipawsArea.utils';
import { isValidMapCoordinate } from '../../../../utils/alertMapEditor.utils';
import {
  getOutsideAreaPathOptions,
  OUTSIDE_AREA_TOOLTIP,
} from '../../../../utils/outsideArea.utils';
import { getNearestPolygonEdgePoint } from '../../../../utils/polygonEditor.utils';

const getShapePathOptions = ({ isSelected, severityColor }) => ({
  color: severityColor,
  fillColor: severityColor,
  fillOpacity: isSelected ? 0.28 : 0.16,
  opacity: 1,
  weight: isSelected ? 4 : 2,
  // Thickness and dashes show selection without relying on color alone.
  dashArray: isSelected ? '8 5' : undefined,
});

const IpawsAreaShapeLayer = ({
  activeTool,
  onSelectShape,
  selectedShapeId,
  severityColor,
  shape,
  editMode,
  canInsertVertex = false,
  onInsertVertex,
  isOutside = false,
}) => {
  const selectShape = (event) => {
    // Do not let selection continue to the map and place another shape underneath this one.
    if (event?.originalEvent) L.DomEvent.stopPropagation(event.originalEvent);
    onSelectShape(shape.id);
  };
  const shapePathOptions = getShapePathOptions({
    isSelected: shape.id === selectedShapeId,
    severityColor,
  });
  const pathOptions = isOutside ? getOutsideAreaPathOptions(shapePathOptions) : shapePathOptions;
  // Leaflet applies the outside class only when it creates the path, so a change remounts it.
  const outsideKey = isOutside ? 'outside' : 'inside';
  const outsideTooltip = isOutside ? <Tooltip sticky>{OUTSIDE_AREA_TOOLTIP}</Tooltip> : null;

  if (shape.type === IPAWS_AREA_TYPES.CIRCLE) {
    if (
      !isValidMapCoordinate(shape.center) ||
      !Number.isFinite(Number(shape.radiusKm)) ||
      Number(shape.radiusKm) <= 0
    ) {
      return null;
    }

    return (
      <Circle
        key={outsideKey}
        center={shape.center}
        eventHandlers={{ click: selectShape }}
        pathOptions={pathOptions}
        // Leaflet expects meters; the editor stores operator-friendly kilometers.
        radius={Number(shape.radiusKm) * 1000}
      >
        {outsideTooltip}
      </Circle>
    );
  }

  const polygonCoordinates = getIpawsAreaPolygonCoordinates(shape);
  if (!polygonCoordinates.length) return null;
  const isAddingVertex =
    shape.id === selectedShapeId && editMode === 'addVertex' && canInsertVertex;

  const polygonEventHandlers = {
    click: (event) => {
      if (event?.originalEvent) L.DomEvent.stopPropagation(event.originalEvent);
      if (isAddingVertex) {
        const nearestEdgePoint = getNearestPolygonEdgePoint(polygonCoordinates, event?.latlng, {
          maximumDistanceMeters: Number.POSITIVE_INFINITY,
        });
        if (nearestEdgePoint) {
          onInsertVertex?.(nearestEdgePoint.insertIndex, nearestEdgePoint.position);
          return;
        }
      }
      onSelectShape(shape.id);
    },
  };

  return (
    <>
      <Polygon
        key={outsideKey}
        positions={polygonCoordinates}
        pathOptions={pathOptions}
        eventHandlers={polygonEventHandlers}
        // While drawing, clicks must reach the map so they add the next polygon point.
        interactive={!(activeTool === IPAWS_AREA_TYPES.POLYGON && shape.id === selectedShapeId)}
      >
        {outsideTooltip}
      </Polygon>
      {isAddingVertex ? (
        <Polygon
          positions={polygonCoordinates}
          pathOptions={{ color: severityColor, opacity: 0.001, weight: 24, fill: false }}
          eventHandlers={polygonEventHandlers}
        />
      ) : null}
    </>
  );
};

export default IpawsAreaShapeLayer;
